import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/button';
import { ExpenseGroup, ExpenseStatus } from '../../../__generated__/graphql';
import { ExpenseGroupStatusInfo } from '../types';
import { ExpenseGroupDeclineModal } from './ExpenseGroupDeclineModal';
import './ExpenseGroupActions.css';

type Props = {
  expenseGroup: ExpenseGroup;
  onStatusChange: (info: ExpenseGroupStatusInfo) => Promise<void>;
};

export const ExpenseGroupActions: React.FC<React.PropsWithChildren<Props>> = ({
  expenseGroup,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const [savingStatus, setSavingStatus] = useState<ExpenseStatus | null>(null);
  const [isReimbursing, setIsReimbursing] = useState<boolean>(false);
  const [decliningGroupId, setDecliningGroupId] = useState<string | null>(null);

  const handleStatusChange = useCallback(
    (status: ExpenseStatus, declineReason?: string, reimbursed?: boolean) => {
      setSavingStatus(status);
      if (reimbursed) {
        setIsReimbursing(true);
      }

      const statusInfo: ExpenseGroupStatusInfo = {
        expenseGroupId: expenseGroup.Id,
        status,
        declineReason,
        reimbursed,
      };

      onStatusChange(statusInfo).finally(() => {
        setSavingStatus(null);
        setIsReimbursing(false);
      });
    },
    [expenseGroup.Id, onStatusChange],
  );

  const handleDeclineClick = useCallback(() => {
    setDecliningGroupId(expenseGroup.Id);
  }, [expenseGroup.Id]);

  const handleDeclineConfirm = useCallback(
    (expenseGroupId: string, reason: string | null) => {
      setDecliningGroupId(null);
      handleStatusChange(ExpenseStatus.Declined, reason || undefined);
    },
    [handleStatusChange],
  );

  const handleDeclineCancel = useCallback(() => {
    setDecliningGroupId(null);
  }, []);

  const isPending = expenseGroup.flair__Status__c === ExpenseStatus.Pending;
  const isApprovedNotReimbursed =
    expenseGroup.flair__Status__c === ExpenseStatus.Approved &&
    !expenseGroup.flair__Reimbursed__c;

  if (!isPending && !isApprovedNotReimbursed) {
    return null;
  }

  return (
    <>
      <div className="d-flex gap-2 expense-group-actions">
        {isPending && (
          <>
            <Button
              label={t('expenses.buttons.decline')}
              isProcessing={savingStatus === ExpenseStatus.Declined}
              disabled={!!savingStatus}
              size="sm"
              variant="outline-danger"
              onClick={handleDeclineClick}
            />
            <Button
              label={t('expenses.buttons.approve')}
              isProcessing={savingStatus === ExpenseStatus.Approved}
              disabled={!!savingStatus}
              size="sm"
              variant="outline-primary"
              onClick={() => handleStatusChange(ExpenseStatus.Approved)}
            />
          </>
        )}
        {(isPending || isApprovedNotReimbursed) && (
          <Button
            label={t('expenses.buttons.reimbursed')}
            isProcessing={isReimbursing}
            disabled={!!savingStatus || isReimbursing}
            size="sm"
            variant="outline-success"
            onClick={() =>
              handleStatusChange(ExpenseStatus.Approved, undefined, true)
            }
          />
        )}
      </div>

      <ExpenseGroupDeclineModal
        expenseGroupId={decliningGroupId}
        inProgress={savingStatus === ExpenseStatus.Declined}
        onClose={handleDeclineCancel}
        onDeclined={handleDeclineConfirm}
      />
    </>
  );
};
